import {
  AppWindow,
  CheckCircle2,
  CircleAlert,
  Headphones,
  Inbox,
  KeyRound,
  Laptop,
  Mail,
  Megaphone,
  MessageCircle,
  Plus,
  TriangleAlert,
  Wrench,
} from "lucide-react";
import { useLayoutEffect, useRef, useState } from "react";
import { Link, Navigate, useLocation } from "react-router";

import type { CatalogueService, ServiceState } from "../api/types";
import { can, useMe } from "../auth/session";
import { useChatLauncher } from "../chat/ChatDrawer";
import { firstName, formatDate, formatDateTime, greeting, plural } from "../lib/format";
import { usePageTitle } from "../lib/hooks";
import { SERVICE_STATES, useAnnouncements } from "../lib/updates";
import { useCatalogue, useTickets } from "../tickets/api";
import { OPEN_STATUSES } from "../tickets/labels";
import { StatusBadge } from "../tickets/TicketBadges";
import { EmptyState, ErrorState } from "../ui/Notice";
import { Skeleton, SkeletonRegion } from "../ui/Skeleton";
import "./home.css";

/** A recognisable icon for each service, by what its name suggests. */
function ServiceIcon({ name }: { name: string }) {
  const n = name.toLowerCase();
  if (/(laptop|hardware|device|printer|monitor|endpoint)/.test(n)) return <Laptop size={20} />;
  if (/(outlook|mail|365|entra|teams)/.test(n)) return <Mail size={20} />;
  if (/(access|password|account|sign|hr)/.test(n)) return <KeyRound size={20} />;
  if (/(phone|headset)/.test(n)) return <Headphones size={20} />;
  return <AppWindow size={20} />;
}

// The normal height of a row of service tiles, in rem (see .home-grid in home.css).
const TILE_ROW_REM = 9.5;

/**
 * The service status card sits level with the first row of tiles. Its height depends on what
 * it says (a warning takes more room), so the tile rows grow to match it, never shrinking
 * below their normal height.
 */
function useTileRowsMatchStatus() {
  const grid = useRef<HTMLDivElement>(null);
  const catalogue = useCatalogue();
  useLayoutEffect(() => {
    const el = grid.current;
    if (!el) return;
    function update() {
      const card = el?.querySelector<HTMLElement>(".status-card");
      const last = card?.lastElementChild;
      if (!el || !card || !last) return;
      const needed =
        last.getBoundingClientRect().bottom - card.getBoundingClientRect().top + parseFloat(getComputedStyle(card).paddingBottom);
      const base = TILE_ROW_REM * parseFloat(getComputedStyle(document.documentElement).fontSize);
      el.style.setProperty("--tile-h", `${Math.ceil(Math.max(base, needed))}px`);
    }
    update();
    window.addEventListener("resize", update);
    const observer = typeof ResizeObserver === "function" ? new ResizeObserver(update) : null;
    const card = el.querySelector(".status-card");
    if (observer && card) observer.observe(card);
    return () => {
      window.removeEventListener("resize", update);
      observer?.disconnect();
    };
  }, [catalogue.data]);
  return grid;
}

/**
 * The desk's front door. Support staff work from Tickets, so their home goes there (keeping a
 * ticket open beside the list, ?open=); everyone else gets the help page.
 */
export function HomePage() {
  const me = useMe();
  const { search } = useLocation();
  if (can(me, "use_workspace")) return <Navigate to={{ pathname: "/tickets", search }} replace />;
  return <PortalHomePage />;
}

/** Help: common requests, service status, your open tickets and news. Staff reach it at /help. */
export function PortalHomePage() {
  const me = useMe();
  usePageTitle("Help");
  return (
    <div className="home">
      <Hero name={firstName(me.display_name)} />
      <HelpGrid />
      <Announcements />
    </div>
  );
}

/** Common requests beside service status and your open tickets, edges aligned. */
function HelpGrid() {
  const grid = useTileRowsMatchStatus();
  return (
    <div className="home-grid" ref={grid}>
      <QuickStart />
      <div className="home-side">
        <ServiceStatus />
        <OpenTickets />
      </div>
    </div>
  );
}

function Hero({ name }: { name: string }) {
  const { openChat } = useChatLauncher();

  return (
    <section className="card card-hero home-hero" aria-labelledby="home-title">
      <div className="home-hero-text">
        <h1 id="home-title" className="home-hero-title">
          {greeting()}, {name}
        </h1>
        <p className="home-hero-lead">Report a problem or request something new.</p>
      </div>
      <div className="home-hero-actions">
        <Link to="/tickets/new" className="btn btn-lg btn-hero">
          <Plus size={18} aria-hidden="true" />
          Raise a ticket
        </Link>
        <button type="button" className="btn btn-lg btn-hero-ghost" onClick={() => openChat()}>
          <MessageCircle size={18} aria-hidden="true" />
          Start a chat
        </button>
      </div>
    </section>
  );
}

/** Common requests: one tile per service, straight into a prefilled form. */
function QuickStart() {
  const catalogue = useCatalogue();
  const services = (catalogue.data ?? []).filter((s) => !s.is_restricted);

  return (
    <section className="home-section home-quick" aria-labelledby="quick-start">
      <div className="home-section-head">
        <h2 id="quick-start" className="section-title">
          Start with a common request
        </h2>
      </div>
      {catalogue.isPending ? (
        <SkeletonRegion label="Loading services" className="tile-grid">
          {Array.from({ length: 6 }, (_, i) => (
            <div key={i} className="tile">
              <Skeleton className="skeleton-icon" />
              <Skeleton width="60%" />
              <Skeleton width="85%" />
            </div>
          ))}
        </SkeletonRegion>
      ) : catalogue.isError ? (
        <div className="card">
          <ErrorState error={catalogue.error} onRetry={() => void catalogue.refetch()} />
        </div>
      ) : services.length === 0 ? (
        <div className="card">
          <EmptyState
            icon={<AppWindow size={24} />}
            title="No services listed yet"
            action={
              <Link to="/tickets/new" className="btn btn-primary">
                Raise a ticket
              </Link>
            }
          >
            Add services in Admin → Services.
          </EmptyState>
        </div>
      ) : (
        <ul className="tile-grid">
          {services.map((service) => (
            <li key={service.id}>
              <ServiceTile service={service} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function ServiceTile({ service }: { service: CatalogueService }) {
  const href = service.slug ? `/tickets/new?service=${encodeURIComponent(service.slug)}` : "/tickets/new";
  const examples = service.categories.slice(0, 3).map((c) => c.name);
  return (
    <Link to={href} className="tile">
      <span className="tile-icon" aria-hidden="true">
        <ServiceIcon name={service.name} />
      </span>
      <span className="tile-name">{service.name}</span>
      <span className="tile-detail">
        {examples.length ? examples.join(", ") : (service.description ?? "Report a problem or ask for something")}
      </span>
    </Link>
  );
}

const STATE_ICONS: Record<Exclude<ServiceState, "operational">, typeof TriangleAlert> = {
  degraded: TriangleAlert,
  outage: CircleAlert,
  maintenance: Wrench,
};

/** Service status: any service marked as not working normally, worst problems first. */
function ServiceStatus() {
  const catalogue = useCatalogue();
  const order: ServiceState[] = ["outage", "degraded", "maintenance"];
  const affected = (catalogue.data ?? [])
    .filter((s) => s.status !== "operational")
    .sort((a, b) => order.indexOf(a.status) - order.indexOf(b.status));

  return (
    <section className="card status-card" aria-labelledby="service-status">
      <h2 id="service-status" className="card-title">
        Service status
      </h2>
      {catalogue.isPending ? (
        <SkeletonRegion label="Loading service status">
          <Skeleton width="70%" />
        </SkeletonRegion>
      ) : affected.length ? (
        <ul className="status-list">
          {affected.map((service) => (
            <AffectedService key={service.id} service={service} />
          ))}
        </ul>
      ) : (
        <div className="status-line status-line-ok">
          <CheckCircle2 size={20} aria-hidden="true" />
          <div>
            <p className="status-line-title">All services running normally</p>
            <p className="status-line-text">If something isn't working for you, raise a ticket.</p>
          </div>
        </div>
      )}
    </section>
  );
}

function AffectedService({ service }: { service: CatalogueService }) {
  const state = SERVICE_STATES[service.status];
  const Icon = STATE_ICONS[service.status as Exclude<ServiceState, "operational">];
  return (
    <li className={`status-line status-line-${state.tone}`}>
      <Icon size={20} aria-hidden="true" />
      <div>
        <p className="status-line-title">
          {service.name}: {state.label.toLowerCase()}
        </p>
        <p className="status-line-text">{service.status_message ?? state.fallback}</p>
        {service.status_updated_at ? (
          <p className="status-line-time">Updated {formatDateTime(service.status_updated_at)}</p>
        ) : null}
      </div>
    </li>
  );
}

function OpenTickets() {
  const open = useTickets({ scope: "mine", status: OPEN_STATUSES, limit: 4 });
  return (
    <section className="card home-tickets" aria-labelledby="my-tickets">
      <div className="card-head">
        <div>
          <h2 id="my-tickets" className="card-title">
            Your open tickets
          </h2>
          {open.data ? <p className="card-sub">{plural(open.data.total, "open ticket", "open tickets")}</p> : null}
        </div>
        <Link to="/tickets?queue=raised" className="link-btn">
          View all
        </Link>
      </div>
      {open.isPending ? (
        <SkeletonRegion label="Loading your tickets" className="skeleton-stack home-list-skeleton">
          <Skeleton width="80%" />
          <Skeleton width="65%" />
          <Skeleton width="72%" />
        </SkeletonRegion>
      ) : open.isError ? (
        <ErrorState error={open.error} onRetry={() => void open.refetch()} />
      ) : open.data.items.length === 0 ? (
        <EmptyState compact icon={<Inbox size={20} />} title="Nothing open">
          Your tickets will appear here.
        </EmptyState>
      ) : (
        <ul className="list-rows">
          {open.data.items.map((ticket) => (
            <li key={ticket.id} className="list-row">
              <Link to={`/tickets/${ticket.reference}`} className="list-row-link">
                <span className="list-row-name">{ticket.title}</span>
                <span className="meta-row">
                  <span>{ticket.reference}</span>
                  <span>Updated {formatDateTime(ticket.last_activity_at)}</span>
                </span>
              </Link>
              <StatusBadge status={ticket.status} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

const SHOWN = 3;

/** News from IT (Admin → Announcements), newest first. */
function Announcements() {
  const announcements = useAnnouncements();
  const [all, setAll] = useState(false);
  const items = announcements.data ?? [];
  const shown = all ? items : items.slice(0, SHOWN);

  return (
    <section className="card" aria-labelledby="announcements">
      <div className="card-head">
        <h2 id="announcements" className="card-title">
          Announcements
        </h2>
      </div>
      {announcements.isPending ? (
        <SkeletonRegion label="Loading announcements" className="skeleton-stack home-list-skeleton">
          <Skeleton width="55%" />
          <Skeleton width="90%" />
          <Skeleton width="75%" />
        </SkeletonRegion>
      ) : announcements.isError ? (
        <ErrorState error={announcements.error} onRetry={() => void announcements.refetch()} />
      ) : items.length === 0 ? (
        <EmptyState compact icon={<Megaphone size={20} />} title="No announcements">
          IT news and planned work will appear here.
        </EmptyState>
      ) : (
        <>
          <ul className="announcement-list">
            {shown.map((a) => (
              <li key={a.id} className="announcement">
                <h3 className="announcement-title">{a.title}</h3>
                <p className="announcement-date">
                  <time dateTime={a.starts_at}>{formatDate(a.starts_at)}</time>
                </p>
                <p className="announcement-body">{a.body}</p>
              </li>
            ))}
          </ul>
          {items.length > SHOWN ? (
            <button type="button" className="link-btn announcement-more" onClick={() => setAll(!all)}>
              {all ? "Show fewer" : `Show ${items.length - SHOWN} more`}
            </button>
          ) : null}
        </>
      )}
    </section>
  );
}
